import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

export interface ServisKaydi {
  id: string;
  created_at: string;
  musteri_id: string;
  cihaz_id: string | null;
  personel_id: string | null;
  servis_tarihi: string;
  servis_saati: string;
  islem_turu: string;
  diger_aciklama: string;
  servis_durumu: string;
  yapilan_islem_aciklamasi: string;
  servis_notu: string;
  alinan_ucret: number;
  odeme_durumu: string;
  odeme_tipi: string;
  bir_sonraki_bakim_tarihi: string | null;
  aktif: boolean;
  musteri_ad?: string;
  musteri_soyad?: string;
  musteri_telefon?: string;
  musteri_adres?: string;
  personel_ad?: string;
  personel_soyad?: string;
}

export const ISLEM_TURLERI = [
  'Filtre Bakımı – Dörtlü',
  'Filtre Bakımı – Beşli',
  'Genel Bakım',
  'Yeni Montaj',
  'Bina Girişi',
  'Bina Girişi Bakım',
  'Taşıma',
  'Diğer',
] as const;

export const ODEME_TIPLERI = ['Nakit', 'Kart', 'IBAN', 'Diğer'] as const;

export const SERVIS_DURUMLARI = [
  { value: 'planlandi', label: 'Planlandı', color: 'bg-sky-100 text-sky-700' },
  { value: 'yolda', label: 'Yolda', color: 'bg-amber-100 text-amber-700' },
  { value: 'islemde', label: 'İşlemde', color: 'bg-orange-100 text-orange-700' },
  { value: 'tamamlandi', label: 'Tamamlandı', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'iptal', label: 'İptal', color: 'bg-red-100 text-red-700' },
];

export const ODEME_DURUMLARI = [
  { value: 'odendi', label: 'Ödendi', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'bekliyor', label: 'Bekliyor', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'iade', label: 'İade', color: 'bg-red-100 text-red-700' },
];

export function useServisKayitlari() {
  const [services, setServices] = useState<ServisKaydi[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterDurum, setFilterDurum] = useState('tumu');
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const fetchServices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from('sadem_servis_kayitlari')
        .select('*, sadem_musteriler!inner(ad, soyad, telefon, acik_adres), sadem_personeller(ad, soyad)')
        .eq('aktif', true)
        .order('servis_tarihi', { ascending: false })
        .order('servis_saati', { ascending: false });

      if (filterDurum !== 'tumu') {
        query = query.eq('servis_durumu', filterDurum);
      }

      const { data, error: err } = await query;

      if (err) throw err;

      const mapped: ServisKaydi[] = (data || []).map((r: any) => ({
        id: r.id,
        created_at: r.created_at,
        musteri_id: r.musteri_id,
        cihaz_id: r.cihaz_id,
        personel_id: r.personel_id,
        servis_tarihi: r.servis_tarihi,
        servis_saati: r.servis_saati,
        islem_turu: r.islem_turu,
        diger_aciklama: r.diger_aciklama,
        servis_durumu: r.servis_durumu,
        yapilan_islem_aciklamasi: r.yapilan_islem_aciklamasi,
        servis_notu: r.servis_notu,
        alinan_ucret: Number(r.alinan_ucret) || 0,
        odeme_durumu: r.odeme_durumu,
        odeme_tipi: r.odeme_tipi,
        bir_sonraki_bakim_tarihi: r.bir_sonraki_bakim_tarihi,
        aktif: r.aktif,
        musteri_ad: r.sadem_musteriler?.ad,
        musteri_soyad: r.sadem_musteriler?.soyad,
        musteri_telefon: r.sadem_musteriler?.telefon,
        musteri_adres: r.sadem_musteriler?.acik_adres,
        personel_ad: r.sadem_personeller?.ad,
        personel_soyad: r.sadem_personeller?.soyad,
      }));

      setServices(mapped);
    } catch (err: any) {
      setError(err.message || 'Servis kayıtları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [filterDurum]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const filteredServices = services.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const ad = `${s.musteri_ad || ''} ${s.musteri_soyad || ''}`.toLowerCase();
    return ad.includes(q) || (s.musteri_telefon || '').includes(q) || s.islem_turu.toLowerCase().includes(q);
  });

  const totalPages = Math.ceil(filteredServices.length / pageSize);
  const paginatedServices = filteredServices.slice((page - 1) * pageSize, page * pageSize);

  const durumCounts = SERVIS_DURUMLARI.reduce((acc, d) => {
    acc[d.value] = services.filter(s => s.servis_durumu === d.value).length;
    return acc;
  }, {} as Record<string, number>);

  return {
    services: paginatedServices,
    allServices: services,
    loading,
    error,
    search,
    setSearch,
    filterDurum,
    setFilterDurum,
    page,
    setPage,
    totalPages,
    totalCount: filteredServices.length,
    durumCounts,
    refetch: fetchServices,
  };
}

export async function createServisKaydi(data: {
  musteri_id: string;
  cihaz_id?: string | null;
  personel_id?: string | null;
  servis_tarihi: string;
  servis_saati: string;
  islem_turu: string;
  diger_aciklama?: string;
  servis_notu?: string;
  alinan_ucret?: number;
  odeme_tipi?: string;
  bir_sonraki_bakim_tarihi?: string | null;
}) {
  const { error } = await supabase.from('sadem_servis_kayitlari').insert({
    musteri_id: data.musteri_id,
    cihaz_id: data.cihaz_id || null,
    personel_id: data.personel_id || null,
    servis_tarihi: data.servis_tarihi,
    servis_saati: data.servis_saati,
    islem_turu: data.islem_turu,
    diger_aciklama: data.diger_aciklama || '',
    servis_durumu: 'planlandi',
    servis_notu: data.servis_notu || '',
    alinan_ucret: data.alinan_ucret || 0,
    odeme_durumu: data.alinan_ucret ? 'odendi' : 'bekliyor',
    odeme_tipi: data.odeme_tipi || 'Nakit',
    bir_sonraki_bakim_tarihi: data.bir_sonraki_bakim_tarihi || null,
    aktif: true,
  });
  if (error) throw error;
}

export async function updateServisKaydi(id: string, data: Partial<ServisKaydi>) {
  const payload: any = {};
  if (data.servis_durumu !== undefined) payload.servis_durumu = data.servis_durumu;
  if (data.personel_id !== undefined) payload.personel_id = data.personel_id;
  if (data.servis_notu !== undefined) payload.servis_notu = data.servis_notu;
  if (data.yapilan_islem_aciklamasi !== undefined) payload.yapilan_islem_aciklamasi = data.yapilan_islem_aciklamasi;
  if (data.alinan_ucret !== undefined) payload.alinan_ucret = data.alinan_ucret;
  if (data.odeme_tipi !== undefined) payload.odeme_tipi = data.odeme_tipi;
  if (data.odeme_durumu !== undefined) payload.odeme_durumu = data.odeme_durumu;
  if (data.bir_sonraki_bakim_tarihi !== undefined) payload.bir_sonraki_bakim_tarihi = data.bir_sonraki_bakim_tarihi;

  const { error } = await supabase.from('sadem_servis_kayitlari').update(payload).eq('id', id);
  if (error) throw error;
}

export async function deleteServisKaydi(id: string) {
  const { error } = await supabase.from('sadem_servis_kayitlari').update({ aktif: false }).eq('id', id);
  if (error) throw error;
}